/**
 * Deployment Verification Configuration
 *
 * Configures how a site reports deployment status to PageMind.
 * Options: Manual (crawl), Publisher Manifest (manifest file), Webhook (client pushes), None
 *
 * Date: 2026-02-13
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';
import { authFetch } from '@/utils/auth-fetch';
import {
  Settings,
  RefreshCw,
  Loader2,
  Copy,
  Eye,
  EyeOff,
  FileText,
  Globe,
  Webhook,
  Ban,
  CheckCircle2,
  Info,
} from 'lucide-react';

// Types
interface DeploymentConfig {
  site_id: string;
  verification_method: 'manual' | 'publisher_manifest' | 'webhook' | 'none';
  webhook_secret: string | null;
  auto_verify_on_publish: boolean;
  verification_frequency: 'hourly' | 'daily' | 'weekly' | 'manual';
  created_at: string | null;
  updated_at: string | null;
}

interface Props {
  siteId: string;
  onMethodChange?: (method: string) => void;
}

const METHOD_INFO = {
  manual: {
    icon: Globe,
    title: 'Manual Crawling',
    description: 'PageMind crawls your pages to verify FAQs are deployed. Use when you cannot provide a manifest file or webhook.',
    color: 'text-blue-500',
  },
  publisher_manifest: {
    icon: FileText,
    title: 'Publisher Manifest',
    description: 'You provide a manifest file (TSV/CSV/JSON) listing published URLs. Best for large catalogs with multi-market URLs.',
    color: 'text-purple-500',
  },
  webhook: {
    icon: Webhook,
    title: 'Webhook',
    description: 'Your system sends deployed URLs to PageMind in real-time. Best for immediate verification.',
    color: 'text-orange-500',
  },
  none: {
    icon: Ban,
    title: 'Disabled',
    description: 'No automatic verification. You can still manually verify individual products.',
    color: 'text-gray-500',
  },
};

export default function DeploymentVerificationConfig({ siteId, onMethodChange }: Props) {
  const [config, setConfig] = useState<DeploymentConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [generatingSecret, setGeneratingSecret] = useState(false);

  // Load config
  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authFetch(`/crawl-intelligence/deployment-verification-config?siteId=${siteId}`);
      if (!response.ok) throw new Error('Failed to load config');
      const data = await response.json();
      if (data.success) {
        setConfig(data.data);
        if (onMethodChange) {
          onMethodChange(data.data.verification_method);
        }
      }
    } catch (error) {
      console.error('Error loading config:', error);
      toast({
        title: 'Error',
        description: 'Failed to load verification configuration',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [siteId, onMethodChange]);

  // Save config
  const saveConfig = async (updates: Partial<DeploymentConfig>) => {
    setSaving(true);
    try {
      const response = await authFetch('/crawl-intelligence/deployment-verification-config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          siteId,
          verificationMethod: updates.verification_method,
          autoVerifyOnPublish: updates.auto_verify_on_publish,
          verificationFrequency: updates.verification_frequency,
        }),
      });

      if (!response.ok) throw new Error('Failed to save config');

      const data = await response.json();
      if (data.success) {
        setConfig(data.data);
        if (onMethodChange && updates.verification_method) {
          onMethodChange(updates.verification_method);
        }
        toast({
          title: 'Success',
          description: 'Configuration saved',
        });
      }
    } catch (error) {
      console.error('Error saving config:', error);
      toast({
        title: 'Error',
        description: 'Failed to save configuration',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Generate webhook secret
  const generateWebhookSecret = async () => {
    setGeneratingSecret(true);
    try {
      const response = await authFetch('/crawl-intelligence/deployment-verification-config/generate-webhook-secret', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ siteId }),
      });

      if (!response.ok) throw new Error('Failed to generate secret');

      const data = await response.json();
      if (data.success) {
        setConfig(data.data);
        setShowSecret(true);
        toast({
          title: 'Success',
          description: 'Webhook secret generated. Make sure to copy it now!',
        });
      }
    } catch (error) {
      console.error('Error generating secret:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate webhook secret',
        variant: 'destructive',
      });
    } finally {
      setGeneratingSecret(false);
    }
  };

  // Copy secret to clipboard
  const copySecret = () => {
    if (config?.webhook_secret) {
      navigator.clipboard.writeText(config.webhook_secret);
      toast({
        title: 'Copied',
        description: 'Webhook secret copied to clipboard',
      });
    }
  };

  // Load on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72 mt-2" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  const currentMethod = config?.verification_method || 'manual';
  const MethodIcon = METHOD_INFO[currentMethod].icon;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex justify-between items-start">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Verification Method
            </CardTitle>
            <CardDescription>
              Configure how PageMind verifies AEO content deployment
            </CardDescription>
          </div>
          <Badge
            variant={currentMethod === 'none' ? 'secondary' : 'default'}
            className="flex items-center gap-1"
          >
            <MethodIcon className={`h-3 w-3 ${METHOD_INFO[currentMethod].color}`} />
            {METHOD_INFO[currentMethod].title}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Method Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {(Object.keys(METHOD_INFO) as Array<keyof typeof METHOD_INFO>).map((method) => {
            const info = METHOD_INFO[method];
            const Icon = info.icon;
            const isSelected = currentMethod === method;

            return (
              <div
                key={method}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/50'
                }`}
                onClick={() => saveConfig({ verification_method: method })}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`h-5 w-5 ${info.color}`} />
                  <span className="font-medium">{info.title}</span>
                  {isSelected && <CheckCircle2 className="h-4 w-4 text-primary ml-auto" />}
                </div>
                <p className="text-xs text-muted-foreground">{info.description}</p>
              </div>
            );
          })}
        </div>

        {/* Webhook Configuration */}
        {currentMethod === 'webhook' && (
          <div className="p-4 bg-muted rounded-lg space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Webhook className="h-5 w-5 text-orange-500" />
              <span className="font-medium">Webhook Configuration</span>
            </div>

            <div className="space-y-2">
              <Label>Webhook URL</Label>
              <div className="flex gap-2">
                <Input
                  value={`${window.location.origin}/api/content-review/webhook/deployment`}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/api/content-review/webhook/deployment`);
                    toast({ title: 'Copied', description: 'Webhook URL copied to clipboard' });
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Send POST requests to this URL when content is deployed
              </p>
            </div>

            <div className="space-y-2">
              <Label>Webhook Secret</Label>
              <div className="flex gap-2">
                <Input
                  value={config?.webhook_secret || ''}
                  type={showSecret ? 'text' : 'password'}
                  readOnly
                  placeholder="No secret generated yet"
                  className="font-mono text-sm"
                />
                {config?.webhook_secret && (
                  <>
                    <Button variant="outline" size="icon" onClick={() => setShowSecret(!showSecret)}>
                      {showSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="icon" onClick={copySecret}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </>
                )}
                <Button
                  variant="outline"
                  onClick={generateWebhookSecret}
                  disabled={generatingSecret}
                >
                  {generatingSecret ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  {config?.webhook_secret ? 'Regenerate' : 'Generate'}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Include this secret in the X-Webhook-Secret header when calling the webhook
              </p>
            </div>

            {/* Webhook Documentation */}
            <div className="space-y-4 border-t pt-4">
              <div className="flex items-center gap-2">
                <Info className="h-5 w-5 text-blue-500" />
                <span className="font-medium">Webhook Integration Guide</span>
              </div>

              {/* Overview */}
              <div className="p-3 bg-background rounded border">
                <h5 className="text-sm font-medium mb-2">Overview</h5>
                <p className="text-xs text-muted-foreground">
                  Send a POST request to the webhook URL whenever you deploy AEO content (FAQs, structured data, etc.)
                  to your product pages. PageMind will queue these URLs for verification and update the deployment status.
                </p>
              </div>

              {/* Request Format */}
              <div className="p-3 bg-background rounded border">
                <h5 className="text-sm font-medium mb-2">Request Format</h5>
                <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
{`POST ${window.location.origin}/api/content-review/webhook/deployment
Content-Type: application/json
X-Webhook-Secret: ${config?.webhook_secret || 'your_secret_here'}

{
  "siteId": "${siteId}",
  "urls": [
    "https://example.com/product/123",
    "https://example.com/product/456"
  ]
}`}
                </pre>

                <div className="mt-3 space-y-2">
                  <div className="text-xs">
                    <span className="font-medium">Headers:</span>
                    <ul className="list-disc list-inside ml-2 text-muted-foreground">
                      <li><code className="bg-muted px-1 rounded">Content-Type</code>: Must be <code className="bg-muted px-1 rounded">application/json</code></li>
                      <li><code className="bg-muted px-1 rounded">X-Webhook-Secret</code>: Your webhook secret (required for authentication)</li>
                    </ul>
                  </div>
                  <div className="text-xs">
                    <span className="font-medium">Body Parameters:</span>
                    <ul className="list-disc list-inside ml-2 text-muted-foreground">
                      <li><code className="bg-muted px-1 rounded">siteId</code> (string, required): Your site ID: <code className="bg-muted px-1 rounded">{siteId}</code></li>
                      <li><code className="bg-muted px-1 rounded">urls</code> (array, required): Array of deployed page URLs (max 1000 per request)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Response Format */}
              <div className="p-3 bg-background rounded border">
                <h5 className="text-sm font-medium mb-2">Response Format</h5>
                <div className="space-y-2">
                  <div className="text-xs">
                    <span className="font-medium text-green-600">Success (200):</span>
                    <pre className="bg-muted p-2 rounded mt-1 overflow-x-auto">
{`{
  "success": true,
  "message": "Queued 2 URLs for verification",
  "data": {
    "queued": 2,
    "urls": [
      "https://example.com/product/123",
      "https://example.com/product/456"
    ]
  }
}`}
                    </pre>
                  </div>

                  <div className="text-xs">
                    <span className="font-medium text-red-600">Error Responses:</span>
                    <ul className="list-disc list-inside ml-2 text-muted-foreground mt-1">
                      <li><code className="bg-muted px-1 rounded">401</code>: Invalid or missing webhook secret</li>
                      <li><code className="bg-muted px-1 rounded">400</code>: Invalid request body (missing siteId or urls)</li>
                      <li><code className="bg-muted px-1 rounded">404</code>: Site not found or webhook not configured</li>
                      <li><code className="bg-muted px-1 rounded">500</code>: Internal server error</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* cURL Example */}
              <div className="p-3 bg-background rounded border">
                <h5 className="text-sm font-medium mb-2">cURL Example</h5>
                <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
{`curl -X POST "${window.location.origin}/api/content-review/webhook/deployment" \\
  -H "Content-Type: application/json" \\
  -H "X-Webhook-Secret: ${config?.webhook_secret || 'your_secret_here'}" \\
  -d '{
    "siteId": "${siteId}",
    "urls": ["https://example.com/product/123"]
  }'`}
                </pre>
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    const curlCommand = `curl -X POST "${window.location.origin}/api/content-review/webhook/deployment" \\
  -H "Content-Type: application/json" \\
  -H "X-Webhook-Secret: ${config?.webhook_secret || 'your_secret_here'}" \\
  -d '{
    "siteId": "${siteId}",
    "urls": ["https://example.com/product/123"]
  }'`;
                    navigator.clipboard.writeText(curlCommand);
                    toast({ title: 'Copied', description: 'cURL command copied to clipboard' });
                  }}
                >
                  <Copy className="h-3 w-3 mr-1" /> Copy cURL command
                </Button>
              </div>

              {/* Multi-Market Support */}
              <div className="p-3 bg-background rounded border">
                <h5 className="text-sm font-medium mb-2">Multi-Market URLs</h5>
                <p className="text-xs text-muted-foreground mb-2">
                  If you deploy the same product to multiple countries/languages, send all variant URLs in a single request.
                  PageMind will group them by product ID and track deployment coverage per market.
                </p>
                <pre className="text-xs bg-muted p-2 rounded overflow-x-auto">
{`{
  "siteId": "${siteId}",
  "urls": [
    "https://example.com/en/product/123",
    "https://example.com/es/product/123",
    "https://example.com/fr/product/123"
  ]
}`}
                </pre>
              </div>

              {/* Best Practices */}
              <div className="p-3 bg-background rounded border">
                <h5 className="text-sm font-medium mb-2">Best Practices</h5>
                <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                  <li>Call the webhook immediately after deploying content to get real-time verification</li>
                  <li>Batch multiple URLs in a single request (up to 1000) for efficiency</li>
                  <li>Store the webhook secret securely - treat it like an API key</li>
                  <li>If you regenerate the secret, update all systems that call this webhook</li>
                  <li>Implement retry logic with exponential backoff for failed requests</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Publisher Manifest hint */}
        {currentMethod === 'publisher_manifest' && (
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="h-5 w-5 text-purple-500" />
              <span className="font-medium">Publisher Manifest</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Configure your manifest sources in the "Publisher Manifest" tab below.
              You can add multiple manifest files for different regions or product types.
            </p>
          </div>
        )}

        {/* Manual crawl hint */}
        {currentMethod === 'manual' && (
          <div className="p-4 bg-muted rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="h-5 w-5 text-blue-500" />
              <span className="font-medium">Manual Crawling</span>
            </div>
            <p className="text-sm text-muted-foreground">
              PageMind will crawl your product pages to verify FAQ deployment.
              Use the "Overview & Batch" tab to run batch verification.
              Note: This method is slower and may trigger rate limiting on your site.
            </p>
          </div>
        )}

        {/* Additional Settings */}
        {currentMethod !== 'none' && (
          <div className="pt-4 border-t space-y-4">
            <h4 className="text-sm font-medium">Additional Settings</h4>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Auto-verify on publish</Label>
                <p className="text-xs text-muted-foreground">
                  Automatically run verification when AEO content is published
                </p>
              </div>
              <Switch
                checked={config?.auto_verify_on_publish || false}
                onCheckedChange={(checked) => saveConfig({ auto_verify_on_publish: checked })}
                disabled={saving}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Verification Frequency</Label>
                <p className="text-xs text-muted-foreground">
                  How often to run scheduled verification
                </p>
              </div>
              <Select
                value={config?.verification_frequency || 'daily'}
                onValueChange={(value) =>
                  saveConfig({ verification_frequency: value as DeploymentConfig['verification_frequency'] })
                }
                disabled={saving}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="manual">Manual Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
